import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, CurrencyCircleDollar, Check, Star } from "@phosphor-icons/react";
import PixCheckoutDialog from "@/components/PixCheckoutDialog";

// PIX + Cartão para planos MENSAIS. Anuais também suportam PIX à vista via Stripe Checkout hosted.
const COMPANY_PLANS = [
  {
    key: "acerto_rapido_monthly",
    name: "Empresa Mensal",
    price: "R$ 59,90",
    amount: 5990,
    period: "/mês",
    description: "Renovação todo mês. Cancele quando quiser.",
    features: [
      "Até 150 veículos + 150 motoristas",
      "Rastreamento em tempo real",
      "Relatórios em PDF e Excel",
      "PIX à vista: avisamos 8 dias antes",
    ],
    supports_pix: true,
    supports_card: true,
    highlight: false,
    testid: "plan-monthly",
  },
  {
    key: "acerto_rapido_annual",
    pix_key: "acerto_rapido_annual_pix",
    pix_amount: 53910,
    name: "Empresa Anual",
    price: "R$ 718,80",
    amount: 71880,
    period: "/ano",
    description: "Pagamento antecipado de 12 meses. 🔥 25% OFF adicional no PIX (economize R$ 179,70).",
    features: [
      "Até 150 veículos + 150 motoristas",
      "12 meses de acesso garantido",
      "Cartão parcelado em até 3x sem juros",
      "🔥 PIX à vista com 25% OFF: R$ 539,10",
    ],
    supports_pix: true,
    supports_card: true,
    installments: 3,
    highlight: true,
    testid: "plan-annual",
  },
];

const AUTONOMO_PLANS = [
  {
    key: "acerto_rapido_autonomo_monthly",
    name: "Autônomo Mensal",
    price: "R$ 25,90",
    amount: 2590,
    period: "/mês",
    description: "Ideal para freteiros e caminhoneiros.",
    features: ["Até 5 veículos", "Todas as funcionalidades", "PIX à vista: avisamos 8 dias antes", "Cancele quando quiser"],
    supports_pix: true,
    supports_card: true,
    highlight: false,
    testid: "plan-autonomo-monthly",
  },
  {
    key: "acerto_rapido_autonomo_annual",
    pix_key: "acerto_rapido_autonomo_annual_pix",
    pix_amount: 23310,
    name: "Autônomo Anual",
    price: "R$ 310,80",
    amount: 31080,
    period: "/ano",
    description: "Pagamento à vista de 12 meses. 🔥 25% OFF adicional no PIX (economize R$ 77,70).",
    features: ["Até 5 veículos", "Todas as funcionalidades", "Cartão parcelado até 3x sem juros", "🔥 PIX à vista com 25% OFF: R$ 233,10"],
    supports_pix: true,
    supports_card: true,
    installments: 3,
    highlight: true,
    testid: "plan-autonomo-annual",
  },
];

export default function PlanSelectDialog({ open, onOpenChange, showAll = false }) {
  const { user } = useAuth();
  const PLANS = showAll
    ? [...AUTONOMO_PLANS, ...COMPANY_PLANS]
    : (user?.role === "autonomo" ? AUTONOMO_PLANS : COMPANY_PLANS);
  const [busy, setBusy] = useState(null);
  const [pixPlan, setPixPlan] = useState(null);

  const payWithCard = async (plan) => {
    setBusy(`card-${plan.key}`);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        lookup_key: plan.key,
        payment_method_types: ["card"],
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no checkout de cartão");
      setBusy(null);
    }
  };

  const payWithPix = (plan) => {
    setPixPlan(plan);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>Escolha seu plano</DialogTitle>
          <DialogDescription>Assine e mantenha acesso completo após o trial de 7 dias.</DialogDescription>
        </DialogHeader>

        <div className={`grid gap-4 mt-2 md:grid-cols-2`}>
          {PLANS.map((p) => (
            <Card key={p.key} className={`p-6 relative ${p.highlight ? "border-2 border-yellow-500 shadow-lg" : "border"}`} data-testid={p.testid}>
              {p.highlight && (
                <Badge className="absolute -top-2 right-4 bg-yellow-500 text-black gap-1"><Star size={12} weight="fill" /> RECOMENDADO</Badge>
              )}
              <div className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold" style={{ fontFamily: "Manrope" }}>{p.price}</span>
                <span className="text-sm text-muted-foreground">{p.period}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{p.description}</p>

              <ul className="mt-4 space-y-1.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check size={16} weight="bold" className="text-emerald-600 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>

              <div className="mt-5 space-y-2">
                {p.supports_pix && (
                  <Button
                    onClick={() => payWithPix(p)}
                    data-testid={`btn-pix-${p.key}`}
                    disabled={busy !== null}
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-2"
                  >
                    <CurrencyCircleDollar size={18} weight="fill" /> Pagar com PIX
                  </Button>
                )}
                {p.supports_card && (
                  <Button
                    onClick={() => payWithCard(p)}
                    disabled={busy === `card-${p.key}`}
                    data-testid={`btn-card-${p.key}`}
                    variant="outline"
                    className="w-full h-11 border-2 border-blue-300 text-blue-700 hover:bg-blue-50 gap-2"
                  >
                    <CreditCard size={18} weight="duotone" />
                    {busy === `card-${p.key}` ? "Redirecionando…" : (p.installments ? `Cartão em até ${p.installments}x` : "Cartão de Crédito/Débito")}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900">
          <strong>Como funciona o PIX à vista mensal:</strong> 8 dias antes do vencimento você recebe e-mail com o QR PIX. Se passar 5 dias do vencimento sem pagamento, o acesso é suspenso automaticamente. Assim que o PIX é confirmado, o acesso volta na hora.
        </div>
      </DialogContent>
    </Dialog>

    {pixPlan && (
      <PixCheckoutDialog
        open={!!pixPlan}
        onOpenChange={(o) => { if (!o) setPixPlan(null); }}
        planKey={pixPlan.pix_key || pixPlan.key}
        planLabel={pixPlan.pix_key ? `${pixPlan.name} — PIX 25% OFF` : pixPlan.name}
        amount={pixPlan.pix_amount || pixPlan.amount}
      />
    )}
    </>
  );
}
